import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { showContactModal } from '../features/authSlice';

export default function HelpModal() {
    const dispatch = useDispatch();
    const isOpen = useSelector((state) => state.channel.isHelpModalOpen);
    const [activeSection, setActiveSection] = useState('getting-started');
    const [readmeContent, setReadmeContent] = useState('');
    const [readmeLoading, setReadmeLoading] = useState(false);
    const [readmeError, setReadmeError] = useState(null);

    // Fetch README content when modal opens
    useEffect(() => {
        if (isOpen && !readmeContent && !readmeLoading) {
            setReadmeLoading(true);
            fetch('/README.md')
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`Failed to load README.md: ${response.status}`);
                    }
                    return response.text();
                })
                .then(content => {
                    setReadmeContent(content);
                    setReadmeError(null);
                })
                .catch(error => {
                    console.warn('Failed to load README.md, falling back to built-in help:', error);
                    setReadmeError(error.message);
                })
                .finally(() => {
                    setReadmeLoading(false);
                });
        }
    }, [isOpen, readmeContent, readmeLoading]);

    // Parse README markdown into sections
    const parseReadmeIntoSections = (content) => {
        if (!content) return null;
        
        const lines = content.split('\n');
        const sections = {};
        let currentSection = null;
        let currentContent = [];
        
        for (const line of lines) {
            if (line.startsWith('## ')) {
                // Save previous section
                if (currentSection && currentContent.length > 0) {
                    sections[currentSection.id] = {
                        title: currentSection.title,
                        content: currentContent.join('\n').trim()
                    };
                }
                
                // Start new section
                const title = line.substring(3).trim();
                const id = title.toLowerCase()
                    .replace(/[^a-z0-9\s-]/g, '')
                    .replace(/\s+/g, '-')
                    .replace(/-+/g, '-')
                    .trim();
                
                currentSection = { id, title };
                currentContent = [];
            } else if (line.startsWith('# ')) {
                // Main title - use as getting-started section
                if (!currentSection) {
                    currentSection = { id: 'getting-started', title: 'Getting Started' };
                }
                currentContent.push(line);
            } else if (currentSection) {
                currentContent.push(line);
            }
        }
        
        // Save final section
        if (currentSection && currentContent.length > 0) {
            sections[currentSection.id] = {
                title: currentSection.title,
                content: currentContent.join('\n').trim()
            };
        }
        
        return sections;
    };

    // Convert markdown content to JSX
    const renderMarkdownContent = (content) => {
        if (!content) return null;
        
        const lines = content.split('\n');
        const elements = [];
        let currentList = null;
        
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            
            if (line.startsWith('# ')) {
                elements.push(<h1 key={i} className="text-3xl font-bold text-white mb-4">{line.substring(2)}</h1>);
            } else if (line.startsWith('### ')) {
                elements.push(<h3 key={i} className="text-lg font-semibold text-white mt-4 mb-2">{line.substring(4)}</h3>);
            } else if (line.startsWith('#### ')) {
                elements.push(<h4 key={i} className="text-md font-semibold text-gray-100 mt-3 mb-2">{line.substring(5)}</h4>);
            } else if (line.match(/^\d+\./)) {
                if (currentList !== 'ol') {
                    if (currentList) elements.push(<br key={`${i}-break`} />);
                    currentList = 'ol';
                }
                const text = line.replace(/^\d+\.\s*/, '');
                elements.push(<li key={i} className="text-gray-300 mb-1">{renderInlineMarkdown(text)}</li>);
            } else if (line.startsWith('- ') || line.startsWith('* ')) {
                if (currentList !== 'ul') {
                    if (currentList) elements.push(<br key={`${i}-break`} />);
                    currentList = 'ul';
                }
                const text = line.replace(/^[-*]\s*/, '');
                elements.push(<li key={i} className="text-gray-300 mb-1">{renderInlineMarkdown(text)}</li>);
            } else if (line.trim() === '') {
                currentList = null;
                elements.push(<br key={i} />);
            } else if (line.trim().length > 0) {
                currentList = null;
                elements.push(<p key={i} className="text-gray-300 mb-2">{renderInlineMarkdown(line)}</p>);
            }
        }
        
        return <div className="space-y-2">{elements}</div>;
    };

    // Render inline markdown (bold, code, etc.)
    const renderInlineMarkdown = (text) => {
        return text
            .split(/(\*\*[^*]+\*\*|`[^`]+`)/)
            .map((part, index) => {
                if (part.startsWith('**') && part.endsWith('**')) {
                    return <strong key={index} className="text-white">{part.slice(2, -2)}</strong>;
                } else if (part.startsWith('`') && part.endsWith('`')) {
                    return <code key={index} className="bg-gray-700 px-1 rounded text-[#3ebaf9]">{part.slice(1, -1)}</code>;
                }
                return part;
            });
    };

    const readmeSections = readmeContent ? parseReadmeIntoSections(readmeContent) : null;

    // Generate help sections from README or use default
    const helpSections = readmeSections ? 
        Object.keys(readmeSections).map(id => ({
            id,
            label: readmeSections[id].title
        })) : [
            { id: 'getting-started', label: 'Getting Started' },
            { id: 'channels', label: 'Channels' },
            { id: 'device-controls', label: 'Device Controls' },
            { id: 'messaging', label: 'Messaging' },
            { id: 'email', label: 'Email' },
            { id: 'agent-chat', label: 'Agent Chat' },
            { id: 'side-panel', label: 'Side Panel' },
            { id: 'configuration', label: 'Configuration' },
            { id: 'troubleshooting', label: 'Troubleshooting' },
        ];

    const helpContent = {
        'getting-started': {
            title: 'Getting Started',
            content: (
                <div className="space-y-6 text-gray-300">
                    <div>
                        <h3 className="text-lg font-semibold mb-3 text-white">Welcome to Try Labb AI</h3>
                        <p className="text-gray-300 mb-4">
                            This demo platform showcases AI-powered customer engagement through multiple channels 
                            including messaging, email, and intelligent agent interactions.
                        </p>
                    </div>
                    
                    <div>
                        <h4 className="font-semibold mb-2 text-gray-100">Quick Start Guide:</h4>
                        <ol className="list-decimal list-inside space-y-2 text-gray-300">
                            <li>Switch between <strong className="text-white">phone</strong> and <strong className="text-white">tablet</strong> views using the device toggle</li>
                            <li>Navigate between channels using the bottom navigation bar</li>
                            <li>Use the <strong className="text-white">Home</strong> button to return to the home screen</li>
                            <li>Access the <strong className="text-white">Side Panel</strong> for employee tools and management</li>
                            <li>Use <strong className="text-white">Action Buttons</strong> for screenshots, sharing, and configuration</li>
                        </ol>
                    </div>

                    <div className="bg-gradient-to-br from-[#44C2FD]/10 to-[#23A6D5]/5 border-2 border-[#44C2FD]/30 p-5 rounded-xl backdrop-blur-sm relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-[#44C2FD]/10 rounded-full blur-2xl"></div>
                        <h4 className="font-semibold text-[#44C2FD] mb-2 flex items-center gap-2 relative z-10">
                            <span className="material-icons text-xl">tips_and_updates</span>
                            Pro Tip
                        </h4>
                        <p className="text-gray-300 text-sm leading-relaxed relative z-10">
                            Click the <strong className="text-white">Follow</strong> action button to enable synchronized scrolling 
                            between customer and employee views.
                        </p>
                    </div>
                </div>
            )
        },
        'channels': {
            title: 'Channels Overview',
            content: (
                <div className="space-y-6">
                    <div>
                        <h3 className="text-lg font-semibold mb-3 text-white">Available Channels</h3>
                        <p className="text-gray-300 mb-4">
                            The platform supports multiple communication channels, each optimized for different interaction types.
                        </p>
                    </div>

                    <div className="grid gap-4">
                        <div className="border border-gray-700/50 bg-gradient-to-br from-gray-700/20 to-gray-800/20 backdrop-blur-sm rounded-xl p-5 hover:border-[#44C2FD]/30 transition-all duration-300">
                            <h4 className="font-semibold mb-2 text-white flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-[#44C2FD]"></div>
                                Messaging (SMS)
                            </h4>
                            <p className="text-gray-300 text-sm leading-relaxed">
                                Real-time text messaging with customers. Supports rich media, quick replies, and automated responses.
                            </p>
                        </div>

                        <div className="border border-gray-700/50 bg-gradient-to-br from-gray-700/20 to-gray-800/20 backdrop-blur-sm rounded-xl p-5 hover:border-[#44C2FD]/30 transition-all duration-300">
                            <h4 className="font-semibold mb-2 text-white flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-[#44C2FD]"></div>
                                Email
                            </h4>
                            <p className="text-gray-300 text-sm leading-relaxed">
                                Full-featured email management with threading, attachments, and template support.
                            </p>
                        </div>

                        <div className="border border-gray-700/50 bg-gradient-to-br from-gray-700/20 to-gray-800/20 backdrop-blur-sm rounded-xl p-5 hover:border-[#44C2FD]/30 transition-all duration-300">
                            <h4 className="font-semibold mb-2 text-white flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-[#44C2FD]"></div>
                                Agent Chat
                            </h4>
                            <p className="text-gray-300 text-sm leading-relaxed">
                                AI-powered chat assistant for intelligent customer interactions and automated responses.
                            </p>
                        </div>

                        <div className="border border-gray-700/50 bg-gradient-to-br from-gray-700/20 to-gray-800/20 backdrop-blur-sm rounded-xl p-5 hover:border-[#44C2FD]/30 transition-all duration-300">
                            <h4 className="font-semibold mb-2 text-white flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-[#44C2FD]"></div>
                                Home Screen
                            </h4>
                            <p className="text-gray-300 text-sm leading-relaxed">
                                iPhone-style home screen with app tiles and customizable background images.
                            </p>
                        </div>
                    </div>
                </div>
            )
        },
        'device-controls': {
            title: 'Device Controls',
            content: (
                <div className="space-y-6">
                    <div>
                        <h3 className="text-lg font-semibold mb-3 text-white">Device Features</h3>
                    </div>

                    <div className="space-y-3">
                        <div className="border-l-4 border-[#44C2FD] pl-5 py-2 bg-[#44C2FD]/5 rounded-r-lg">
                            <h4 className="font-semibold mb-2 text-white">Device Toggle</h4>
                            <p className="text-gray-300 text-sm leading-relaxed">
                                Switch between phone and tablet views to test responsive designs and different screen sizes.
                                Located in the top status bar.
                            </p>
                        </div>

                        <div className="border-l-4 border-green-500 pl-5 py-2 bg-green-500/5 rounded-r-lg">
                            <h4 className="font-semibold mb-2 text-white">Home Button</h4>
                            <p className="text-gray-300 text-sm leading-relaxed">
                                Returns to the home screen from any channel. Features realistic iPhone-style animations 
                                and glossy button design.
                            </p>
                        </div>

                        <div className="border-l-4 border-purple-500 pl-5 py-2 bg-purple-500/5 rounded-r-lg">
                            <h4 className="font-semibold mb-2 text-white">Status Bar</h4>
                            <p className="text-gray-300 text-sm leading-relaxed">
                                Shows current time, signal strength, battery indicator, and device controls. 
                                Realistic iPhone-style status indicators.
                            </p>
                        </div>

                        <div className="border-l-4 border-orange-500 pl-5 py-2 bg-orange-500/5 rounded-r-lg">
                            <h4 className="font-semibold mb-2 text-white">Navigation Bar</h4>
                            <p className="text-gray-300 text-sm leading-relaxed">
                                Bottom navigation with channel buttons and action controls. Smooth animations 
                                and visual feedback for interactions.
                            </p>
                        </div>
                    </div>
                </div>
            )
        },
        'messaging': {
            title: 'Messaging Features',
            content: (
                <div className="space-y-6">
                    <div>
                        <h3 className="text-lg font-semibold mb-3 text-white">SMS Messaging</h3>
                        <p className="text-gray-300 mb-4">
                            Real-time messaging capabilities with modern chat interface and advanced features.
                        </p>
                    </div>

                    <div className="space-y-4">
                        <div className="border border-gray-700/50 bg-gradient-to-br from-gray-700/20 to-gray-800/20 backdrop-blur-sm rounded-xl p-5">
                            <h4 className="font-semibold mb-3 text-white flex items-center gap-2">
                                <span className="material-icons text-[#44C2FD]">check_circle</span>
                                Key Features
                            </h4>
                            <ul className="space-y-2 text-gray-300 text-sm">
                                <li className="flex items-start gap-2">
                                    <span className="text-[#44C2FD] mt-0.5">•</span>
                                    <span>Real-time message delivery and read receipts</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#44C2FD] mt-0.5">•</span>
                                    <span>Rich media support (images, files, etc.)</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#44C2FD] mt-0.5">•</span>
                                    <span>Automated responses and quick replies</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#44C2FD] mt-0.5">•</span>
                                    <span>Message threading and conversation history</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#44C2FD] mt-0.5">•</span>
                                    <span>Typing indicators and delivery status</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#44C2FD] mt-0.5">•</span>
                                    <span>Emoji and rich text formatting support</span>
                                </li>
                            </ul>
                        </div>

                        <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 border-2 border-amber-500/30 p-5 rounded-xl backdrop-blur-sm">
                            <h4 className="font-semibold text-amber-400 mb-3 flex items-center gap-2">
                                <span className="material-icons text-xl">lightbulb</span>
                                Usage Tips
                            </h4>
                            <ul className="text-gray-300 text-sm space-y-2">
                                <li className="flex items-start gap-2">
                                    <span className="text-amber-400 mt-0.5">•</span>
                                    <span>Use the text input at the bottom to send messages</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-amber-400 mt-0.5">•</span>
                                    <span>Messages appear in real-time chat bubbles</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-amber-400 mt-0.5">•</span>
                                    <span>Scroll up to view conversation history</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-amber-400 mt-0.5">•</span>
                                    <span>Long press on messages for additional options</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            )
        },
        'email': {
            title: 'Email Management',
            content: (
                <div className="space-y-6">
                    <div>
                        <h3 className="text-lg font-semibold mb-3 text-white">Email Platform</h3>
                        <p className="text-gray-300 mb-4">
                            Comprehensive email management with modern interface and powerful features.
                        </p>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <h4 className="font-semibold mb-2 text-gray-100">Email Features:</h4>
                            <ul className="list-disc list-inside space-y-1 text-gray-600 text-sm">
                                <li>Full email composition with rich text editor</li>
                                <li>Attachment support for files and images</li>
                                <li>Email threading and conversation grouping</li>
                                <li>Search and filtering capabilities</li>
                                <li>Template management for quick responses</li>
                                <li>Priority marking and categorization</li>
                            </ul>
                        </div>

                        <div className="bg-green-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-green-800 mb-2">Best Practices</h4>
                            <ul className="text-green-700 text-sm space-y-1">
                                <li>• Use clear, descriptive subject lines</li>
                                <li>• Keep messages concise and professional</li>
                                <li>• Utilize templates for common responses</li>
                                <li>• Mark urgent emails with appropriate priority</li>
                            </ul>
                        </div>
                    </div>
                </div>
            )
        },
        'agent-chat': {
            title: 'Agent Chat',
            content: (
                <div className="space-y-6">
                    <div>
                        <h3 className="text-lg font-semibold mb-3 text-white">AI Agent Assistant</h3>
                        <p className="text-gray-300 mb-4">
                            Intelligent chat agent powered by AI for automated customer interactions and support.
                        </p>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <h4 className="font-semibold mb-2 text-gray-100">Agent Capabilities:</h4>
                            <ul className="list-disc list-inside space-y-1 text-gray-600 text-sm">
                                <li>Natural language processing and understanding</li>
                                <li>Automated response generation</li>
                                <li>Context-aware conversations</li>
                                <li>Integration with knowledge base</li>
                                <li>Escalation to human agents when needed</li>
                                <li>Multi-language support</li>
                            </ul>
                        </div>

                        <div className="bg-[#3ebaf9]/10 border border-[#3ebaf9]/30 p-4 rounded-lg">
                            <h4 className="font-semibold text-[#3ebaf9] mb-2">AI Features</h4>
                            <ul className="text-[#3ebaf9]/90 text-sm space-y-1">
                                <li>• Learns from conversation patterns</li>
                                <li>• Provides intelligent suggestions</li>
                                <li>• Handles common queries automatically</li>
                                <li>• Maintains conversation context</li>
                            </ul>
                        </div>
                    </div>
                </div>
            )
        },
        'side-panel': {
            title: 'Side Panel',
            content: (
                <div className="space-y-6">
                    <div>
                        <h3 className="text-lg font-semibold mb-3 text-white">Employee Side Panel</h3>
                        <p className="text-gray-300 mb-4">
                            Chrome-style browser window for employee tools, management interfaces, and system controls.
                        </p>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <h4 className="font-semibold mb-2 text-gray-100">Panel Features:</h4>
                            <ul className="list-disc list-inside space-y-1 text-gray-600 text-sm">
                                <li>Chrome-style window controls (close, minimize, fullscreen)</li>
                                <li>Navigation bar with back/forward/refresh buttons</li>
                                <li>Address bar showing current URL</li>
                                <li>Expandable and collapsible interface</li>
                                <li>Fullscreen mode for detailed work</li>
                                <li>Integration with Pega systems</li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="font-semibold mb-2 text-gray-100">Window Controls:</h4>
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                    <span className="text-sm text-gray-600">Close - Closes the side panel</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                                    <span className="text-sm text-gray-600">Minimize - Collapses the panel</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                    <span className="text-sm text-gray-600">Fullscreen - Expands to full screen</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        'configuration': {
            title: 'Configuration',
            content: (
                <div className="space-y-6">
                    <div>
                        <h3 className="text-lg font-semibold mb-3 text-white">System Configuration</h3>
                        <p className="text-gray-300 mb-4">
                            Customize and configure the platform to match your organization's needs and brand.
                        </p>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <h4 className="font-semibold mb-2 text-gray-100">Configuration Options:</h4>
                            <ul className="list-disc list-inside space-y-1 text-gray-600 text-sm">
                                <li>Channel settings and visibility controls</li>
                                <li>Brand and color scheme customization</li>
                                <li>Logo and background image settings</li>
                                <li>Server and API endpoint configuration</li>
                                <li>Employee panel and side panel settings</li>
                                <li>Default messages and email templates</li>
                            </ul>
                        </div>

                        <div className="bg-purple-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-purple-800 mb-2">⚙️ Configuration Tips</h4>
                            <ul className="text-purple-700 text-sm space-y-1">
                                <li>• Export configuration for backup and sharing</li>
                                <li>• Import pre-configured settings</li>
                                <li>• Test changes before applying to production</li>
                                <li>• Use the reset function to restore defaults</li>
                            </ul>
                        </div>
                    </div>
                </div>
            )
        },
        'troubleshooting': {
            title: 'Troubleshooting',
            content: (
                <div className="space-y-6">
                    <div>
                        <h3 className="text-lg font-semibold mb-3 text-white">Common Issues & Solutions</h3>
                        <p className="text-gray-300 mb-4">
                            Quick fixes for common problems and troubleshooting guidelines.
                        </p>
                    </div>

                    <div className="space-y-6">
                        <div>
                            <h4 className="font-semibold mb-3 text-gray-100">Frequently Asked Questions</h4>
                            
                            <div className="space-y-4">
                                <div className="border rounded-lg p-4">
                                    <h5 className="font-semibold mb-2 text-white">Messages not appearing?</h5>
                                    <p className="text-gray-300 text-sm">
                                        Check your internet connection and refresh the page. Ensure the messaging service is properly configured in settings.
                                    </p>
                                </div>

                                <div className="border rounded-lg p-4">
                                    <h5 className="font-semibold mb-2 text-white">Side panel not loading?</h5>
                                    <p className="text-gray-300 text-sm">
                                        Verify the employee panel URL in configuration. Check for CORS issues if loading external content.
                                    </p>
                                </div>

                                <div className="border rounded-lg p-4">
                                    <h5 className="font-semibold mb-2 text-white">Device animations laggy?</h5>
                                    <p className="text-gray-300 text-sm">
                                        Ensure hardware acceleration is enabled in your browser. Close unnecessary tabs and applications.
                                    </p>
                                </div>

                                <div className="border rounded-lg p-4">
                                    <h5 className="font-semibold mb-2 text-white">Configuration not saving?</h5>
                                    <p className="text-gray-300 text-sm">
                                        Check browser storage permissions. Clear cache and cookies, then try again. Export configuration as backup.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-red-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-red-800 mb-2">🚨 Need More Help?</h4>
                            <p className="text-red-700 text-sm">
                                If you're still experiencing issues, try the <strong>Reset</strong> function to restore 
                                default settings, or contact your system administrator for technical support.
                            </p>
                        </div>
                    </div>
                </div>
            )
        },
    };

    const handleClose = () => {
        dispatch({ type: 'channel/toggleHelpModal' });
    };

    if (!isOpen) return null;

    // Detect mobile viewport
    const isMobileView = typeof window !== 'undefined' && window.innerWidth < 1024; // Include tablets

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10000]"
                        onClick={handleClose}
                    />

                    {/* Modal - Sliding from right like AuthPane, full screen on mobile */}
                    <motion.div
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className={`fixed top-0 bottom-0 bg-[#1C2837] z-[10001] cara-chrome overflow-hidden flex flex-col shadow-2xl ${
                            isMobileView ? 'left-0 right-0 w-full' : 'right-0 w-[70%] lg:w-[60%]'
                        }`}
                        style={{
                            fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif',
                            background: `
                                radial-gradient(circle at 80% 20%, rgba(68, 194, 253, 0.08) 0%, transparent 50%),
                                radial-gradient(circle at 20% 80%, rgba(35, 166, 213, 0.06) 0%, transparent 50%),
                                linear-gradient(180deg, #1C2837 0%, #1C2837 100%)
                            `,
                        }}
                    >
                            {/* Header with enhanced styling */}
                            <div className="bg-gradient-to-r from-[#141D2B] via-[#1C2837] to-[#141D2B] px-4 sm:px-6 py-5 border-b border-gray-700/50 flex items-center justify-between flex-shrink-0 relative overflow-hidden">
                                {/* Subtle animated shine effect */}
                                <div 
                                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"
                                    style={{
                                        animation: 'shine 3s ease-in-out infinite',
                                        transform: 'translateX(-100%)',
                                    }}
                                />
                                <style>{`
                                    @keyframes shine {
                                        0%, 100% { transform: translateX(-100%); }
                                        50% { transform: translateX(100%); }
                                    }
                                `}</style>
                                
                                <div className="flex items-center gap-3 relative z-10">
                                    <div>
                                        <h2 className="text-xl font-semibold text-white">Help & Documentation</h2>
                                        <p className="text-gray-400 text-sm">Learn how to use the platform</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 relative z-10">
                                    <button
                                        onClick={() => dispatch(showContactModal())}
                                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[#2C3544] text-gray-300 border border-gray-600/50 hover:border-[#44C2FD]/60 hover:text-white transition-all duration-200"
                                    >
                                        <span className="material-icons text-lg">email</span>
                                        Contact
                                    </button>
                                    <button
                                        onClick={handleClose}
                                        className="text-gray-400 hover:text-white transition-all duration-200 p-2 hover:bg-gray-700/50 rounded-full hover:rotate-90"
                                    >
                                        <span className="material-icons">close</span>
                                    </button>
                                </div>
                            </div>

                {/* Mobile: Dropdown selector */}
                {isMobileView ? (
                    <div className="flex-1 overflow-y-auto overflow-x-hidden bg-[#1C2837] flex flex-col">
                        <div className="p-4 border-b border-gray-700/50 bg-[#141D2B]">
                            <label className="block text-sm font-medium text-gray-400 tracking-wide mb-2">
                                Select Topic
                            </label>
                            <select
                                value={activeSection}
                                onChange={(e) => setActiveSection(e.target.value)}
                                className="w-full bg-[#1C2837] text-white border-2 border-gray-600/50 rounded-lg px-4 py-3 text-sm focus:ring-4 focus:ring-[#44C2FD]/30 focus:border-[#44C2FD] transition-all duration-300 shadow-lg"
                            >
                                {helpSections.map((section) => (
                                    <option key={section.id} value={section.id}>
                                        {section.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4">
                            <div className="max-w-4xl">
                                {readmeLoading ? (
                                    <div className="flex items-center justify-center py-12">
                                        <div className="text-center">
                                            <div className="relative w-16 h-16 mx-auto mb-6">
                                                <div className="absolute inset-0 rounded-full border-4 border-gray-700"></div>
                                                <div className="absolute inset-0 rounded-full border-4 border-[#44C2FD] border-t-transparent animate-spin"></div>
                                                <div className="absolute inset-2 rounded-full bg-[#44C2FD]/10"></div>
                                            </div>
                                            <p className="text-gray-300 font-medium">Loading documentation...</p>
                                            <p className="text-gray-500 text-sm mt-2">Please wait</p>
                                        </div>
                                    </div>
                                ) : readmeSections ? (
                                    // Use README content
                                    <div>
                                        <h1 className="text-2xl font-bold text-white mb-4">
                                            {readmeSections[activeSection]?.title || 'Documentation'}
                                        </h1>
                                        <div className="prose prose-invert max-w-none">
                                            {readmeSections[activeSection] ? 
                                                renderMarkdownContent(readmeSections[activeSection].content) : 
                                                <p className="text-gray-400">Section not found.</p>
                                            }
                                        </div>
                                    </div>
                                ) : readmeError ? (
                                    // Fallback to built-in content if README fails
                                    <div>
                                        <div className="mb-6 p-4 bg-yellow-900/50 border border-yellow-700 rounded-lg">
                                            <p className="text-yellow-300 text-sm">
                                                Could not load README.md ({readmeError}). Showing built-in help content.
                                            </p>
                                        </div>
                                        <h1 className="text-2xl font-bold text-white mb-2">
                                            {helpContent[activeSection]?.title}
                                        </h1>
                                        <div className="prose prose-invert max-w-none">
                                            {helpContent[activeSection]?.content}
                                        </div>
                                    </div>
                                ) : (
                                    // Default built-in content
                                    <div>
                                        <h1 className="text-2xl font-bold text-white mb-2">
                                            {helpContent[activeSection]?.title}
                                        </h1>
                                        <div className="prose prose-invert max-w-none">
                                            {helpContent[activeSection]?.content}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    /* Desktop: Sidebar navigation */
                    <div className="flex flex-1 overflow-hidden">
                        {/* Navigation Sidebar with enhanced design */}
                        <div className="w-80 bg-[#141D2B] border-r border-gray-700/50 overflow-y-auto overflow-x-hidden">
                            <div className="p-4">
                                <h3 className="text-xs font-medium text-gray-500 tracking-wide mb-4 px-3">
                                    Documentation
                                </h3>
                                <nav className="space-y-1.5">
                                    {helpSections.map((section, index) => (
                                        <motion.button
                                            key={section.id}
                                            onClick={() => setActiveSection(section.id)}
                                            initial={{ opacity: 0, x: -10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ 
                                                delay: index * 0.03,
                                                duration: 0.3,
                                                ease: [0.25, 0.46, 0.45, 0.94]
                                            }}
                                            whileHover={{ x: 4 }}
                                            className={`w-full text-left px-4 py-3 rounded-lg text-sm transition-all duration-200 flex items-center gap-3 group ${
                                                activeSection === section.id
                                                    ? 'bg-gradient-to-r from-[#44C2FD] to-[#23A6D5] text-white font-semibold shadow-lg shadow-[#44C2FD]/30'
                                                    : 'text-gray-300 hover:bg-[#1C2837] hover:text-white'
                                            }`}
                                        >
                                            <div className={`w-1.5 h-1.5 rounded-full transition-all ${
                                                activeSection === section.id 
                                                    ? 'bg-white' 
                                                    : 'bg-gray-600 group-hover:bg-[#44C2FD]'
                                            }`} />
                                            <span>{section.label}</span>
                                        </motion.button>
                                    ))}
                                </nav>
                            </div>
                        </div>

                        {/* Content Area with enhanced background */}
                        <div className="flex-1 overflow-y-auto overflow-x-hidden bg-gradient-to-br from-[#1C2837] to-[#141D2B] relative">
                            {/* Subtle background pattern */}
                            <div className="absolute inset-0 opacity-5 pointer-events-none">
                                <div className="absolute top-0 right-0 w-96 h-96 bg-[#44C2FD] rounded-full filter blur-3xl animate-pulse"></div>
                            </div>
                            <div className="p-8 relative z-10">
                                <div className="max-w-4xl">
                                    {readmeLoading ? (
                                        <div className="flex items-center justify-center py-12">
                                            <div className="text-center">
                                                <div className="relative w-16 h-16 mx-auto mb-6">
                                                    <div className="absolute inset-0 rounded-full border-4 border-gray-700"></div>
                                                    <div className="absolute inset-0 rounded-full border-4 border-[#44C2FD] border-t-transparent animate-spin"></div>
                                                    <div className="absolute inset-2 rounded-full bg-[#44C2FD]/10"></div>
                                                </div>
                                                <p className="text-gray-300 font-medium">Loading documentation...</p>
                                                <p className="text-gray-500 text-sm mt-2">Please wait</p>
                                            </div>
                                        </div>
                                    ) : readmeSections ? (
                                        // Use README content
                                        <div>
                                            <h1 className="text-3xl font-bold text-white mb-4">
                                                {readmeSections[activeSection]?.title || 'Documentation'}
                                            </h1>
                                            <div className="prose prose-invert max-w-none">
                                                {readmeSections[activeSection] ? 
                                                    renderMarkdownContent(readmeSections[activeSection].content) : 
                                                    <p className="text-gray-400">Section not found.</p>
                                                }
                                            </div>
                                        </div>
                                    ) : readmeError ? (
                                        // Fallback to built-in content if README fails
                                        <div>
                                            <div className="mb-6 p-4 bg-yellow-900/50 border border-yellow-700 rounded-lg">                                                    <p className="text-yellow-300 text-sm">
                                                        Could not load README.md ({readmeError}). Showing built-in help content.
                                                    </p>
                                            </div>
                                            <h1 className="text-3xl font-bold text-white mb-2">
                                                {helpContent[activeSection]?.title}
                                            </h1>
                                            <div className="prose prose-invert max-w-none">
                                                {helpContent[activeSection]?.content}
                                            </div>
                                        </div>
                                    ) : (
                                        // Default built-in content
                                        <div>
                                            <h1 className="text-3xl font-bold text-white mb-2">
                                                {helpContent[activeSection]?.title}
                                            </h1>
                                            <div className="prose prose-invert max-w-none">
                                                {helpContent[activeSection]?.content}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
